import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaymentTypeToken, EnumToken } from '@sleed/bizcommerce-api';
import { paymentTypesApi, binaryRepoImagesApi } from '~/lib/api/client';
import { formToToken, type PaymentTypeFormValues } from '~/lib/schemas/payment-type';

export const paymentTypeKeys = {
	all: ['payment-types'] as const,
	list: () => [...paymentTypeKeys.all, 'list'] as const,
	detail: (id: number | string) => [...paymentTypeKeys.all, 'detail', id] as const,
	onlineTypes: () => [...paymentTypeKeys.all, 'online-types'] as const
};

export function usePaymentTypesList() {
	return useQuery<PaymentTypeToken[]>({
		queryKey: paymentTypeKeys.list(),
		queryFn: async () => (await paymentTypesApi.getAll()).data.items ?? []
	});
}

/** Single payment type by id (full record incl. description/image/customer-groups). `create` → null. */
export function usePaymentType(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<PaymentTypeToken | null>({
		queryKey: paymentTypeKeys.detail(id),
		queryFn: async () => (enabled ? (await paymentTypesApi.getById(numericId)).data : null),
		enabled
	});
}

/** Online payment provider types (EnumToken {id, nm}) for the `opt` dropdown + grid resolution. */
export function useOnlinePaymentTypes() {
	return useQuery<EnumToken[]>({
		queryKey: paymentTypeKeys.onlineTypes(),
		queryFn: async () => (await paymentTypesApi.getOnlineTypes()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

/**
 * Upload a payment-type image into slot `key` ("1"). The payment type must already exist (id > 0);
 * the File is attached as form-data keyed by the slot subkey (mirrors the manufacturer upload + the
 * legacy BinaryRepoImages/UploadPaymentType contract).
 */
export async function uploadPaymentTypeImage(paymentTypeId: number, key: '1', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadPaymentType(paymentTypeId, key, { data: fd });
}

export function usePaymentTypeMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: paymentTypeKeys.all });
	const create = useMutation({
		mutationFn: async (form: PaymentTypeFormValues) => (await paymentTypesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: PaymentTypeFormValues) => (await paymentTypesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await paymentTypesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
